import React, { useState, useMemo, useRef } from 'react';
import { Modal, Alert, Row, Col, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import api from '../services/api';

const MAIN = '#6d4e36';

/* ── champs cibles disponibles ── */
const DB_FIELDS = [
  { key: 'societe',            label: 'Société' },
  { key: 'operateur',         label: 'Opérateur' },
  { key: 'tel',               label: 'Téléphone' },
  { key: 'colonne1',          label: 'Type (col. 1)' },
  { key: 'nom_prenom',        label: 'Nom & Prénom' },
  { key: 'qualification',     label: 'Qualification' },
  { key: 'statut',            label: 'Statut' },
  { key: 'bureau',            label: 'Bureau' },
  { key: 'forfait',           label: 'Forfait' },
  { key: 'prix',              label: 'Prix' },
  { key: 'observation',       label: 'Observation' },
  { key: 'ancien_utilisateur',label: 'Ancien utilisateur' },
];

/* ── détection automatique header → champ ── */
const AUTO_MAP = {
  'operateur': 'operateur', 'opérateur': 'operateur',
  'tel': 'tel', 'téléphone': 'tel', 'telephone': 'tel', 'numero': 'tel', 'numéro': 'tel',
  'colonne 1': 'colonne1', 'colonne1': 'colonne1', 'col 1': 'colonne1', 'type': 'colonne1',
  'nom et prenom': 'nom_prenom', 'nom & prenom': 'nom_prenom', 'nom_prenom': 'nom_prenom',
  'nom prenom': 'nom_prenom', 'nom et prénom': 'nom_prenom', 'prénom': 'nom_prenom',
  'qualification': 'qualification',
  'statut': 'statut',
  'bureau': 'bureau',
  'forfait': 'forfait',
  'prix': 'prix', 'tarif': 'prix', 'montant': 'prix',
  'observation': 'observation', 'observations': 'observation', 'remarque': 'observation',
  'ancien utilisateur': 'ancien_utilisateur', 'ancien_utilisateur': 'ancien_utilisateur',
  'ancien user': 'ancien_utilisateur', 'ancienusateur': 'ancien_utilisateur',
  'societe': 'societe', 'société': 'societe',
};

const normalize = s => String(s || '').toLowerCase().trim()
  .normalize('NFD').replace(/[̀-ͯ]/g, '');

const autoDetect = header => AUTO_MAP[normalize(header)] || '';

/* ── helpers styles ── */
const thSt = {
  background: `linear-gradient(135deg, ${MAIN} 0%, #8b5e3c 100%)`,
  color: '#fff', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase',
  letterSpacing: '0.04em', padding: '8px 10px', whiteSpace: 'nowrap'
};
const tdSt = { padding: '6px 10px', fontSize: '0.8rem', verticalAlign: 'middle', borderBottom: '1px solid #f5ede8' };
const selSt = {
  border: '1px solid #d8c8b8', borderRadius: 6, fontSize: '0.82rem',
  padding: '4px 8px', width: '100%', background: '#fff'
};
const btnSm = (color, bg = 'transparent') => ({
  border: `1px solid ${color}`, background: bg,
  color: bg === 'transparent' ? color : '#fff',
  borderRadius: 6, padding: '5px 14px', fontSize: '0.82rem',
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600
});

/* ── composant principal ── */
export default function TelephoneImportModal({ show, onHide, onSuccess }) {
  const fileRef = useRef();
  const [step,       setStep]       = useState(1);
  const [headers,    setHeaders]    = useState([]);
  const [rawRows,    setRawRows]    = useState([]);
  const [sheetNames, setSheetNames] = useState([]);
  const [sheetIdx,   setSheetIdx]   = useState(0);
  const [workbook,   setWorkbook]   = useState(null);
  const [mapping,    setMapping]    = useState({});
  const [error,      setError]      = useState('');
  const [importing,  setImporting]  = useState(false);
  const [result,     setResult]     = useState(null);

  const reset = () => {
    setStep(1); setHeaders([]); setRawRows([]); setSheetNames([]);
    setSheetIdx(0); setWorkbook(null); setMapping({}); setError(''); setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => { reset(); onHide(); };

  /* ── parse une feuille ── */
  const parseSheet = (wb, idx) => {
    const ws = wb.Sheets[wb.SheetNames[idx]];
    const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    const hdrs = (data[0] || []).map(h => String(h).trim());
    const rows = data.slice(1).filter(r => r.some(c => c !== '' && c !== undefined));
    return { hdrs, rows };
  };

  /* ── lecture du fichier ── */
  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array' });
        setWorkbook(wb);
        setSheetNames(wb.SheetNames);
        const { hdrs, rows } = parseSheet(wb, 0);
        if (hdrs.length === 0) { setError('Fichier vide ou non reconnu'); return; }
        applySheet(hdrs, rows);
        setSheetIdx(0);
        setStep(2);
      } catch {
        setError('Impossible de lire ce fichier Excel');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const applySheet = (hdrs, rows) => {
    setHeaders(hdrs);
    setRawRows(rows);
    const auto = {};
    hdrs.forEach(h => { auto[h] = autoDetect(h); });
    setMapping(auto);
  };

  const changeSheet = idx => {
    if (!workbook) return;
    const { hdrs, rows } = parseSheet(workbook, idx);
    setSheetIdx(idx);
    applySheet(hdrs, rows);
  };

  /* ── données mappées ── */
  const mappedRows = useMemo(() => {
    return rawRows.map(row => {
      const obj = {};
      headers.forEach((h, i) => {
        const field = mapping[h];
        if (field) obj[field] = row[i] !== undefined ? String(row[i]).trim() : '';
      });
      return obj;
    });
  }, [rawRows, headers, mapping]);

  const validRows   = mappedRows.filter(r => r.tel);
  const ignoredRows = mappedRows.length - validRows.length;

  /* ── import ── */
  const handleImport = async () => {
    if (validRows.length === 0) { setError('Aucune ligne valide (colonne Téléphone manquante ou non mappée)'); return; }
    setImporting(true);
    setError('');
    try {
      const res = await api.post('/telephones/import', { rows: validRows });
      setResult(res.data);
      setStep(3);
      onSuccess();
    } catch (e) {
      setError(e?.response?.data?.message || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  /* ── rendu ── */
  const steps = ['Fichier', 'Mapping', 'Résultat'];

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton style={{ borderBottom: '2px solid #f5ede8' }}>
        <Modal.Title style={{ color: MAIN, fontWeight: 700, fontSize: '1.05rem' }}>
          📥 Import Excel — Numéros Téléphones
        </Modal.Title>
      </Modal.Header>

      {/* Stepper */}
      <div style={{ display: 'flex', borderBottom: '1px solid #f5ede8', background: '#fdf9f7' }}>
        {steps.map((s, i) => (
          <div key={s} style={{
            flex: 1, textAlign: 'center', padding: '10px 0',
            fontWeight: step === i + 1 ? 700 : 400,
            color: step === i + 1 ? MAIN : step > i + 1 ? '#2e7d32' : '#bbb',
            borderBottom: step === i + 1 ? `3px solid ${MAIN}` : '3px solid transparent',
            fontSize: '0.85rem', transition: 'all 0.2s'
          }}>
            <span style={{ marginRight: 6, fontWeight: 700 }}>{i + 1}.</span>{s}
            {step > i + 1 && <span style={{ marginLeft: 6, color: '#2e7d32' }}>✓</span>}
          </div>
        ))}
      </div>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {error && <Alert variant="danger" className="py-2 mb-3">{error}</Alert>}

        {/* ── STEP 1 : Upload ── */}
        {step === 1 && (
          <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>📂</div>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: MAIN, marginBottom: 6 }}>
              Choisir un fichier Excel
            </div>
            <div style={{ color: '#aaa', fontSize: '0.82rem', marginBottom: 20 }}>
              Formats supportés : .xlsx, .xls, .csv
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv"
              onChange={handleFile}
              style={{ display: 'none' }} id="xl-upload" />
            <label htmlFor="xl-upload" style={{
              ...btnSm(MAIN, MAIN), cursor: 'pointer',
              padding: '8px 24px', fontSize: '0.9rem'
            }}>
              📁 Parcourir…
            </label>
          </div>
        )}

        {/* ── STEP 2 : Mapping ── */}
        {step === 2 && (
          <div>
            {/* Choix feuille si plusieurs */}
            {sheetNames.length > 1 && (
              <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: MAIN }}>Feuille :</span>
                {sheetNames.map((s, i) => (
                  <button key={s} onClick={() => changeSheet(i)}
                    style={{
                      ...btnSm(MAIN, i === sheetIdx ? MAIN : 'transparent'),
                      padding: '3px 12px', fontSize: '0.8rem'
                    }}>{s}</button>
                ))}
              </div>
            )}

            {/* Stats */}
            <div style={{
              background: '#f5ede8', borderRadius: 8, padding: '10px 16px',
              marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: '0.83rem'
            }}>
              <span><strong style={{ color: MAIN }}>{rawRows.length}</strong> lignes détectées</span>
              <span><strong style={{ color: MAIN }}>{headers.length}</strong> colonnes</span>
              <span><strong style={{ color: '#2e7d32' }}>{validRows.length}</strong> lignes valides (avec tél)</span>
              {ignoredRows > 0 && <span style={{ color: '#e67e22' }}><strong>{ignoredRows}</strong> sans tél (ignorées)</span>}
            </div>

            {/* Table de mapping */}
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '36%' }} />
                  <col style={{ width: '36%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={thSt}>Colonne Excel</th>
                    <th style={thSt}>Aperçu (3 premières valeurs)</th>
                    <th style={thSt}>Champ à importer</th>
                  </tr>
                </thead>
                <tbody>
                  {headers.map((h, i) => {
                    const preview = rawRows.slice(0, 3)
                      .map(r => String(r[i] || '').trim())
                      .filter(Boolean)
                      .join(' · ');
                    const isMapped = !!mapping[h];
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fdf9f7' }}>
                        <td style={{ ...tdSt, fontWeight: 600, color: MAIN }}>{h || `(col. ${i + 1})`}</td>
                        <td style={{ ...tdSt, color: '#888', fontStyle: preview ? 'normal' : 'italic' }}>
                          {preview || '—'}
                        </td>
                        <td style={tdSt}>
                          <select
                            style={{ ...selSt, borderColor: isMapped ? '#2e7d32' : '#d8c8b8', color: isMapped ? '#2e7d32' : '#888' }}
                            value={mapping[h] || ''}
                            onChange={e => setMapping(m => ({ ...m, [h]: e.target.value }))}
                          >
                            <option value="">— Ignorer —</option>
                            {DB_FIELDS.map(f => (
                              <option key={f.key} value={f.key}>{f.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Aperçu données mappées */}
            {validRows.length > 0 && (
              <>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Aperçu des 5 premières lignes mappées
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead>
                      <tr>
                        {DB_FIELDS.filter(f => Object.values(mapping).includes(f.key)).map(f => (
                          <th key={f.key} style={{ ...thSt, fontSize: '0.7rem', padding: '6px 8px' }}>{f.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {validRows.slice(0, 5).map((r, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fdf9f7' }}>
                          {DB_FIELDS.filter(f => Object.values(mapping).includes(f.key)).map(f => (
                            <td key={f.key} style={{ ...tdSt, padding: '5px 8px' }}>{r[f.key] || '—'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── STEP 3 : Résultat ── */}
        {step === 3 && result && (
          <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>
              {result.skipped === 0 ? '✅' : '⚠️'}
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: MAIN, marginBottom: 16 }}>
              Import terminé
            </div>
            <Row className="g-3 justify-content-center">
              <Col xs={4}>
                <div style={{ background: '#e8f5e9', borderRadius: 10, padding: '16px 10px', color: '#2e7d32' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800 }}>{result.created}</div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>Créés</div>
                </div>
              </Col>
              {result.skipped > 0 && (
                <Col xs={4}>
                  <div style={{ background: '#fde8ea', borderRadius: 10, padding: '16px 10px', color: '#dc3545' }}>
                    <div style={{ fontSize: '1.8rem', fontWeight: 800 }}>{result.skipped}</div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>Ignorés (erreur)</div>
                  </div>
                </Col>
              )}
              <Col xs={4}>
                <div style={{ background: '#f5ede8', borderRadius: 10, padding: '16px 10px', color: MAIN }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800 }}>{result.total}</div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>Total traités</div>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: '1px solid #f5ede8', gap: 8 }}>
        {step === 1 && (
          <button style={btnSm('#888')} onClick={handleClose}>Annuler</button>
        )}
        {step === 2 && (
          <>
            <button style={btnSm('#888')} onClick={() => { reset(); }}>← Recommencer</button>
            <button style={btnSm('#888')} onClick={handleClose}>Annuler</button>
            <button
              onClick={handleImport}
              disabled={importing || validRows.length === 0}
              style={{ ...btnSm(MAIN, MAIN), fontWeight: 700, padding: '6px 20px', opacity: validRows.length === 0 ? 0.5 : 1 }}
            >
              {importing ? <><Spinner size="sm" animation="border" /> Import en cours…</> : `📥 Importer ${validRows.length} ligne${validRows.length !== 1 ? 's' : ''}`}
            </button>
          </>
        )}
        {step === 3 && (
          <button style={{ ...btnSm(MAIN, MAIN), fontWeight: 700 }} onClick={handleClose}>
            Fermer
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
